import { ShakaP2PEngine } from "p2p-media-loader-shaka";
import shaka from "shaka-player/dist/shaka-player.ui";

// P2P statistics tracking
export interface P2PStats {
  peersConnected: number;
  bytesDownloadedP2P: number;
  bytesDownloadedHTTP: number;
  bytesUploaded: number;
}

let stats: P2PStats = {
  peersConnected: 0,
  bytesDownloadedP2P: 0,
  bytesDownloadedHTTP: 0,
  bytesUploaded: 0,
};

// Track connected peer IDs
const connectedPeers = new Set<string>();

// Track real segment download timings measured by the P2P core.
// Keyed by segment URL; exposed via getSegmentLoadDuration() so the
// Shaka response filter can override the loader's rounded timeMs.
const segmentStartTimes = new Map<string, number>();
const segmentDurations = new Map<string, number>();

export function getSegmentLoadDuration(url: string): number | undefined {
  return segmentDurations.get(url);
}

export function consumeSegmentLoadDuration(url: string): number | undefined {
  const d = segmentDurations.get(url);
  segmentDurations.delete(url);
  return d;
}

// Track segments currently held in the p2p-media-loader core's memory cache.
// Used to surface "P2P buffered ahead" in the nerd stats panel.
interface P2PSegment { endTime: number; bytes: number }
const p2pSegments = new Map<string, P2PSegment>();

export function getP2PBufferAhead(currentTime: number): { seconds: number; bytes: number; count: number } {
  let maxEnd = currentTime;
  let bytes = 0;
  let count = 0;
  for (const [url, s] of p2pSegments) {
    if (s.endTime < currentTime - 30) {
      p2pSegments.delete(url);
      continue;
    }
    if (s.endTime > currentTime) {
      if (s.endTime > maxEnd) maxEnd = s.endTime;
      bytes += s.bytes;
      count++;
    }
  }
  return { seconds: maxEnd - currentTime, bytes, count };
}

// Callback for stats changes
type StatsCallback = (stats: P2PStats) => void;
let statsCallback: StatsCallback | null = null;

export function onStatsChange(callback: StatsCallback): void {
  statsCallback = callback;
}

export function getP2PStats(): P2PStats {
  return { ...stats };
}

export function resetP2PStats(): void {
  stats = {
    peersConnected: 0,
    bytesDownloadedP2P: 0,
    bytesDownloadedHTTP: 0,
    bytesUploaded: 0,
  };
  connectedPeers.clear();
  statsCallback?.(stats);
}

// Register Shaka plugins - must be called before creating player
export function registerP2PPlugins(): void {
  // Pass shaka instance since we're using ES modules (not global)
  // Type assertion needed due to shaka-player.ui vs shaka-player.compiled type mismatch
  ShakaP2PEngine.registerPlugins(shaka as unknown as Parameters<typeof ShakaP2PEngine.registerPlugins>[0]);
}

// Create and configure P2P engine for a video
export function createP2PEngine(videoId: string): ShakaP2PEngine {
  const engine = new ShakaP2PEngine(
    {
      core: {
        // Use video ID as swarm ID so viewers of the same video connect
        swarmId: `dtube-${videoId}`,
        // Announce to DTube tracker
        announceTrackers: ["wss://tracker.d.tube"],
        // Cap the in-memory segment cache (MiB). Library defaults reach
        // 4 GiB on desktop, which gets long-movie tabs OOM-killed; the
        // download windows self-throttle as this fills, so P2P keeps
        // working within the cap.
        segmentMemoryStorageLimit: 512,
      },
    },
    // Pass shaka instance for ES module usage (type assertion for ui vs compiled mismatch)
    shaka as unknown as ConstructorParameters<typeof ShakaP2PEngine>[1]
  );

  // Set up event listeners
  engine.addEventListener("onPeerConnect", (params: { peerId: string }) => {
    if (!connectedPeers.has(params.peerId)) {
      connectedPeers.add(params.peerId);
      stats.peersConnected = connectedPeers.size;
      statsCallback?.(stats);
    }
  });

  engine.addEventListener("onPeerClose", (params: { peerId: string }) => {
    if (connectedPeers.has(params.peerId)) {
      connectedPeers.delete(params.peerId);
      stats.peersConnected = connectedPeers.size;
      statsCallback?.(stats);
    }
  });

  engine.addEventListener("onChunkDownloaded", (bytesLength: number, downloadSource: string) => {
    if (downloadSource === "p2p") {
      stats.bytesDownloadedP2P += bytesLength;
    } else {
      stats.bytesDownloadedHTTP += bytesLength;
    }
    statsCallback?.(stats);
  });

  engine.addEventListener("onChunkUploaded", (bytesLength: number) => {
    stats.bytesUploaded += bytesLength;
    statsCallback?.(stats);
  });

  const pendingSegments = new Map<string, { endTime: number }>();

  engine.addEventListener("onSegmentStart", (params: { segment: { url: string; endTime: number } }) => {
    segmentStartTimes.set(params.segment.url, Date.now());
    pendingSegments.set(params.segment.url, { endTime: params.segment.endTime });
  });

  engine.addEventListener("onSegmentLoaded", (params: { segmentUrl: string; bytesLength: number }) => {
    const start = segmentStartTimes.get(params.segmentUrl);
    if (start !== undefined) {
      segmentDurations.set(params.segmentUrl, Date.now() - start);
      segmentStartTimes.delete(params.segmentUrl);
    }
    const pending = pendingSegments.get(params.segmentUrl);
    if (pending) {
      p2pSegments.set(params.segmentUrl, { endTime: pending.endTime, bytes: params.bytesLength });
      pendingSegments.delete(params.segmentUrl);
    }
  });

  return engine;
}
